// src/pages/TermsOfService.js
import React from 'react';
import { ArrowLeft, FileText, Users, Shield, CreditCard, MessageCircle, AlertTriangle, CheckCircle } from 'lucide-react';

const TermsOfService = () => {
  const handleGoBack = () => {
    window.history.back();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-blue-100">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center gap-4">
            <button 
              onClick={handleGoBack}
              className="flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Back</span>
            </button>
            <div className="flex items-center gap-3">
              <FileText className="w-8 h-8 text-blue-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Terms of Service</h1>
                <p className="text-sm text-gray-600">Rules and guidelines for using our app</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-xl border border-blue-100 overflow-hidden">
          {/* Hero Section */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-8 text-white">
            <div className="flex items-center gap-4 mb-4">
              <Shield className="w-8 h-8" />
              <h2 className="text-3xl font-bold">Agreement to Terms</h2>
            </div>
            <p className="text-lg text-blue-100">
              By using ArabicLearning, you agree to these terms of service. These terms ensure a safe, 
              educational environment for children and families learning Arabic language and culture.
            </p>
            <div className="mt-6 flex flex-wrap gap-4 text-sm">
              <div className="bg-white/20 rounded-full px-4 py-2">
                <strong>Effective Date:</strong> September 1, 2025
              </div>
              <div className="bg-white/20 rounded-full px-4 py-2">
                <strong>Last Updated:</strong> September 1, 2025
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="px-8 py-8">
            
            {/* Service Description */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Description of Service</h2>
              <p className="text-gray-700 mb-6">TheOliveTree is an educational app and online Arabic course that provides:</p>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-purple-50 rounded-lg p-4">
                  <h4 className="font-semibold text-purple-800 flex items-center gap-2">
                    🎮 <span>Interactive Arabic language learning online courses and games</span>
                  </h4>
                </div>
                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-800 flex items-center gap-2">
                    🌍 <span>Cultural education about Arabic-speaking countries</span>
                  </h4>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <h4 className="font-semibold text-green-800 flex items-center gap-2">
                    📊 <span>Progress tracking and analytics for parents</span>
                  </h4>
                </div>
                <div className="bg-orange-50 rounded-lg p-4">
                  <h4 className="font-semibold text-orange-800 flex items-center gap-2">
                    🤖 <span>Limited AI chat functionality for educational purposes</span>
                  </h4>
                </div>
              </div>
            </div>

            {/* Parental Supervision */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-red-100 p-3 rounded-full">
                  <Users className="w-6 h-6 text-red-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-800 m-0">Parental Supervision Required</h2>
              </div>

              <div className="bg-red-50 border-l-4 border-red-400 p-6">
                <h3 className="text-xl font-semibold text-red-800 mb-4">You acknowledge and agree that:</h3>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                    <span className="text-red-700">Children should use this app under parental supervision</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                    <span className="text-red-700">You are responsible for monitoring your child's app usage</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                    <span className="text-red-700">You will review and approve all app features before allowing access</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                    <span className="text-red-700">You will regularly check your child's progress and interactions</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Subscription Terms */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-green-100 p-3 rounded-full">
                  <CreditCard className="w-6 h-6 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-800 m-0">Subscription Terms and Pricing</h2>
              </div>

              <div className="space-y-6">
                {/*
                <div className="bg-green-50 rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-green-800 mb-4">Free Tier</h3>
                  <ul className="list-disc list-inside text-green-700 space-y-1">
                    <li>Limited access to select games and features</li>
                    <li>Basic progress tracking</li>
                    <li>Limited daily usage</li>
                  </ul>
                </div>
                

                <div className="bg-blue-50 rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-blue-800 mb-4">Premium Subscription</h3>
                  <ul className="list-disc list-inside text-blue-700 space-y-1">
                    <li>Full access to all games and activities</li>
                    <li>Comprehensive progress analytics</li>
                    <li>Unlimited daily usage</li>
                    <li>Priority customer support</li>
                  </ul>
                </div>
                */}

                <div className="bg-purple-50 rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-purple-800 mb-4">Subscription Details</h3>
                  <ul className="list-disc list-inside text-purple-700 space-y-1">
                    <li>Subscriptions are billed every 6 weeks</li>
                    <li>Prices are subject to change with 30 days notice</li>
                    <li>Renewals available 1 week before your course ends</li>
                  </ul>
                </div>

                  <div className="bg-red-50 rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-red-800 mb-4">Course Cancellation & Refund Policy</h3>
                    <ul className="list-disc list-inside text-red-700 space-y-2">
                      <li>
                        <strong>Before the Course Begins:</strong>
                        <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                          <li>Cancellations made more than 48 hours before the first class: Full refund.</li>
                          <li>Cancellations made within 48 hours of the first class: Full refund minus an administration fee of 20% of the course fee or $20 (whichever is greater).</li>
                        </ul>
                      </li>
                      <li>
                        <strong>After the Course Begins:</strong>
                        <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                          <li>After attending 1 class: 80% refund of the course fee.</li>
                          <li>After attending 2 classes: 50% refund of the course fee.</li>
                          <li>After attending 3 or more classes: No refunds will be issued.</li>
                        </ul>
                      </li>
                      <li>
                        <strong>Missed Classes:</strong> Missed classes are non-refundable and non-transferable.
                      </li>
                      <li>
                        <strong>Emergency Situations:</strong> In the case of a serious and unavoidable emergency, a credit may be issued at our discretion.
                      </li>
                      <li>
                        <strong>Refund Processing:</strong> Approved refunds will be issued to the original method of payment within a reasonable timeframe.
                      </li>
                    </ul>
                  </div>
              </div>
            </div>

            {/* AI Chat Terms */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-purple-100 p-3 rounded-full">
                  <MessageCircle className="w-6 h-6 text-purple-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-800 m-0">AI Chat Feature Terms</h2>
              </div>

              <div className="bg-purple-50 border-l-4 border-purple-400 p-6">
                <h3 className="text-xl font-semibold text-purple-800 mb-4">Educational AI Assistant</h3>
                <p className="text-purple-700 mb-6">Our app includes a limited AI chat feature designed exclusively for Arabic language learning. By using this feature, you agree that:</p>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      Permitted Uses:
                    </h4>
                    <ul className="list-disc list-inside text-green-700 ml-7 space-y-1">
                      <li>Asking questions about Arabic letters, numbers, and basic vocabulary</li>
                      <li>Requesting pronunciation help</li>
                      <li>Getting hints for educational games</li>
                      <li>Learning about Arabic culture and traditions</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold text-red-800 mb-3 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5" />
                      Prohibited Uses:
                    </h4>
                    <ul className="list-disc list-inside text-red-700 ml-7 space-y-1">
                      <li>Sharing personal information (names, addresses, school info)</li>
                      <li>Discussing non-educational topics</li>
                      <li>Attempting to bypass safety filters</li>
                      <li>Using inappropriate language</li>
                      <li>Trying to extract non-educational information</li>
                    </ul>
                  </div>

                  <div className="bg-blue-100 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-800 mb-3">Safety Measures:</h4>
                    <ul className="list-disc list-inside text-blue-700 space-y-1">
                      <li>All AI interactions are monitored and filtered</li>
                      <li>Conversations are not stored or saved</li>
                      <li>Parents can disable AI chat at any time</li>
                      <li>AI responses are designed for child safety and education</li>
                      <li>Inappropriate content is automatically blocked</li>
                    </ul>
                  </div>

                  <div className="bg-green-100 rounded-lg p-4">
                    <h4 className="font-semibold text-green-800 mb-3">Parental Controls:</h4>
                    <ul className="list-disc list-inside text-green-700 space-y-1">
                      <li>Parents can review AI chat settings</li>
                      <li>Parents can completely disable AI functionality</li>
                      <li>Parents receive notifications of any safety concerns</li>
                      <li>Parents can report inappropriate AI responses</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* User Responsibilities */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">User Responsibilities</h2>
              
              <div className="space-y-6">
                <div className="bg-blue-50 rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-blue-800 mb-4">Parents/Guardians Must:</h3>
                  <ul className="list-disc list-inside text-blue-700 space-y-1">
                    <li>Supervise children's use of the app</li>
                    <li>Ensure account information remains accurate</li>
                    <li>Protect login credentials</li>
                    <li>Monitor subscription charges</li>
                    <li>Report any safety concerns immediately</li>
                    <li>Respect intellectual property rights</li>
                  </ul>
                </div>

                <div className="bg-green-50 rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-green-800 mb-4">Children Must:</h3>
                  <ul className="list-disc list-inside text-green-700 space-y-1">
                    <li>Use the app for educational purposes only</li>
                    <li>Follow all safety guidelines</li>
                    <li>Not share personal information</li>
                    <li>Treat the app and other users respectfully</li>
                    <li>Ask parents for permission before making any changes</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Prohibited Uses */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Prohibited Uses</h2>
              
              <div className="bg-red-50 border-l-4 border-red-400 p-6">
                <p className="text-red-800 mb-4 font-medium">You may not use our app to:</p>
                <div className="grid md:grid-cols-2 gap-4">
                  <ul className="list-disc list-inside text-red-700 space-y-2">
                    <li>Share or collect personal information from children</li>
                    <li>Upload inappropriate content</li>
                    <li>Attempt to hack or disrupt the service</li>
                    <li>Use the app for commercial purposes</li>
                  </ul>
                  <ul className="list-disc list-inside text-red-700 space-y-2">
                    <li>Violate any applicable laws or regulations</li>
                    <li>Circumvent safety features or parental controls</li>
                    <li>Reverse engineer or copy our software</li>
                    <li>Create multiple accounts to bypass limitations</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Educational Disclaimer */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Educational Disclaimer</h2>
              
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6">
                <h3 className="text-xl font-semibold text-yellow-800 mb-4">Important Understanding:</h3>
                <ul className="list-disc list-inside text-yellow-700 space-y-2">
                  <li>This app is a supplementary educational tool</li>
                  <li>It does not replace formal Arabic language instruction</li>
                  <li>Learning outcomes may vary between children</li>
                  <li>We do not guarantee specific educational results</li>
                  <li>Parents should supplement app learning with other resources</li>
                </ul>
              </div>
            </div>

            {/* Limitation of Liability */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Limitation of Liability</h2>
              
              <div className="space-y-6">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-gray-800 mb-4">To the fullest extent permitted by law:</h3>
                  <ul className="list-disc list-inside text-gray-700 space-y-1">
                    <li>We provide the app "as is" without warranties</li>
                    <li>We are not liable for any indirect or consequential damages</li>
                    <li>Our liability is limited to the amount paid for the service</li>
                    <li>We are not responsible for device damage or data loss</li>
                    <li>Educational outcomes are not guaranteed</li>
                  </ul>
                </div>

                <div className="bg-blue-50 rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-blue-800 mb-4">Exceptions:</h3>
                  <ul className="list-disc list-inside text-blue-700 space-y-1">
                    <li>Nothing limits our liability for fraud or willful misconduct</li>
                    <li>Consumer protection laws may provide additional rights</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Termination */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Termination</h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-green-50 rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-green-800 mb-4">You may terminate by:</h3>
                  <ul className="list-disc list-inside text-green-700 space-y-1">
                    <li>Canceling your subscription through account settings</li>
                    <li>Contacting customer support</li>
                    <li>Deleting the app from your device</li>
                  </ul>
                </div>

                <div className="bg-orange-50 rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-orange-800 mb-4">We may terminate if:</h3>
                  <ul className="list-disc list-inside text-orange-700 space-y-1">
                    <li>You violate these Terms</li>
                    <li>You provide false information</li>
                    <li>Your account remains inactive for extended periods</li>
                    <li>We discontinue the service</li>
                  </ul>
                </div>
              </div>

              <div className="bg-purple-100 rounded-lg p-6 mt-6">
                <h3 className="text-xl font-semibold text-purple-800 mb-4">Upon termination:</h3>
                <ul className="list-disc list-inside text-purple-700 space-y-1">
                  <li>Access to premium features ends immediately</li>
                  <li>Your data will be deleted according to our Privacy Policy</li>
                  <li>Outstanding payments remain due</li>
                </ul>
              </div>
            </div>

            {/* Changes to Terms */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Changes to Terms</h2>
              
              <div className="bg-indigo-50 rounded-lg p-6">
                <p className="text-indigo-700 mb-4">We may update these Terms to reflect:</p>
                <ul className="list-disc list-inside text-indigo-700 mb-6 space-y-1">
                  <li>Changes in our services</li>
                  <li>New legal requirements</li>
                  <li>Improved user protections</li>
                </ul>
                
                <h4 className="font-semibold text-indigo-800 mb-3">When we update Terms:</h4>
                <ul className="list-disc list-inside text-indigo-700 space-y-1">
                  <li>We'll notify you via email or app notification</li>
                  <li>Continued use constitutes acceptance of new Terms</li>
                  <li>Major changes require new consent</li>
                  <li>You can terminate if you disagree with changes</li>
                </ul>
              </div>
            </div>

            {/* Contact */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Contact Information</h2>
              
              <div className="bg-gradient-to-r from-blue-100 to-purple-100 rounded-lg p-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-semibold text-blue-800 mb-3">Questions about Terms</h3>
                    <p className="text-blue-700">📧 admin@my-roots.ca</p>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold text-blue-800 mb-3">Customer Support</h3>
                    <p className="text-blue-700">📧 admin@my-roots.ca</p>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold text-blue-800 mb-3">Phone</h3>
                    <p className="text-blue-700">📞 +1-343-333-7579</p>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold text-blue-800 mb-3">Mail</h3>
                    <p className="text-blue-700">📮 North Vancouver, BC, Canada</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Acknowledgment */}
            <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-lg p-8 text-center">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Acknowledgment</h2>
              <p className="text-gray-700 mb-4">By using TheOliveTree, you acknowledge that:</p>
              <div className="grid md:grid-cols-2 gap-4 text-left">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="text-gray-700 text-sm">You have read and understood these Terms</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="text-gray-700 text-sm">You agree to be bound by these Terms</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="text-gray-700 text-sm">You have the authority to enter into this agreement</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="text-gray-700 text-sm">You understand the educational nature of our service</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="text-gray-700 text-sm">You accept responsibility for supervising your child's use</span>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* Footer Note */}
        <div className="mt-8 text-center">
          <p className="text-gray-600">
            These Terms are designed to ensure a safe, educational, and enjoyable experience for children and families learning Arabic. 
            If you have any questions, please contact us.
          </p>
        </div>
      </main>
    </div>
  );
};

export default TermsOfService;